import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import toast from 'react-hot-toast';
import { Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ValuationTypeCategoryAssignment = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('valuation_type_category_assignment');
  const [assignments, setAssignments] = useState([]);
  const [valuationTypes, setValuationTypes] = useState([]);
  const [valuationCategories, setValuationCategories] = useState([]);
  const [plants, setPlants] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedPlant, setSelectedPlant] = useState('');

  useEffect(() => {
    loadAssignments();
    loadValuationTypes();
    loadValuationCategories();
    loadPlants();
  }, []);

  const loadAssignments = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/valuation-type-category-assignments`);
      setAssignments(response.data || []);
    } catch (error) {
      console.error('Error loading assignments:', error);
      toast.error('Failed to load assignments');
    } finally {
      setIsLoading(false);
    }
  };

  const loadValuationTypes = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/valuation-types`);
      setValuationTypes(response.data || []);
    } catch (error) {
      console.error('Error loading valuation types:', error);
    }
  };

  const loadValuationCategories = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/valuation-categories`);
      setValuationCategories(response.data || []);
    } catch (error) {
      console.error('Error loading valuation categories:', error);
    }
  };

  const loadPlants = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/material-management/plants`);
      setPlants(response.data || []);
    } catch (error) {
      console.error('Error loading plants:', error);
    }
  };

  const handleOpenDialog = () => {
    setSelectedType('');
    setSelectedCategory('');
    setSelectedPlant('');
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedType || !selectedCategory || !selectedPlant) {
      toast.error('Please select Valuation Type, Category, and Plant');
      return;
    }

    const data = {
      valuation_type_id: selectedType,
      valuation_category_id: selectedCategory,
      plant_id: selectedPlant
    };

    try {
      await axios.post(`${BACKEND_URL}/api/valuation-type-category-assignments`, data);
      toast.success('Assignment created successfully');
      setIsDialogOpen(false);
      loadAssignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create assignment');
    }
  };

  const handleDelete = async (typeId, categoryId, plantId) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) {
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/valuation-type-category-assignments/${typeId}/${categoryId}/${plantId}`);
      toast.success('Assignment deleted successfully');
      loadAssignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete assignment');
    }
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Valuation Type to Category Assignments</h1>
            <p className="text-sm text-gray-500 mt-1">Assign valuation types to valuation categories</p>
          </div>
        </div>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Assignments</h2>
            {writable && (<Button onClick={handleOpenDialog} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Create Assignment
            </Button>)}
          </div>

          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : assignments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No assignments found. Create one to get started.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valuation Type ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valuation Category ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plant</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {assignments.map((assignment, index) => {
                    const plant = plants.find(p => p.plant_id === assignment.plant_id);
                    return (
                    <tr key={`${assignment.valuation_type_id}-${assignment.valuation_category_id}-${assignment.plant_id}-${index}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{assignment.valuation_type_id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{assignment.valuation_category_id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{plant ? `${plant.plant_id} - ${plant.plant_name}` : assignment.plant_id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {writable && (<Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(assignment.valuation_type_id, assignment.valuation_category_id, assignment.plant_id)}
                          className="flex items-center gap-1 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </Button>)}
                      </td>
                    </tr>
                  )})}

                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Assignment</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="valuation_type_id">Valuation Type *</Label>
                <Select value={selectedType} onValueChange={setSelectedType} required>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select Valuation Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {valuationTypes.map((type) => (
                      <SelectItem key={type.valuation_type_id} value={type.valuation_type_id}>
                        {type.valuation_type_id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="valuation_category_id">Valuation Category *</Label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory} required>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select Valuation Category" />
                  </SelectTrigger>
                  <SelectContent>
                    {valuationCategories.map((category) => (
                      <SelectItem key={category.valuation_category_id} value={category.valuation_category_id}>
                        {category.valuation_category_id} - {category.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="plant_id">Plant *</Label>
                <Select value={selectedPlant} onValueChange={setSelectedPlant} required>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select Plant" />
                  </SelectTrigger>
                  <SelectContent>
                    {plants.map((plant) => (
                      <SelectItem key={plant.plant_id} value={plant.plant_id}>
                        {plant.plant_id} - {plant.plant_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  Create
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ValuationTypeCategoryAssignment;
